import React from "react";
import { Select, InputNumber, Button, Tooltip } from "antd";
import { MinusCircleOutlined, MenuOutlined } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { formatDuration } from "../../utils/Misc";

const { Option } = Select;

const MediaSelector = ({
  mediaOptions,
  selectedMedia,
  setSelectedMedia,
  showErrors,
  handleMedia,
}) => {
  const handleChange = (values) => {
    const updated = values.map((val) => {
      const existing = selectedMedia.find((m) => m.value === val);
      if (existing) return existing;
      const option = mediaOptions.find((m) => m.value === val);
      return { ...option, duration: 5 }; // default duration
    });
    setSelectedMedia(updated);
    handleMedia(updated);
  };

  const handleDurationChange = (index, value) => {
    const updated = [...selectedMedia];
    updated[index].duration = value;
    setSelectedMedia(updated);
    handleMedia(updated);
  };

  const handleRemove = (index) => {
    const updated = [...selectedMedia];
    updated.splice(index, 1);
    setSelectedMedia(updated);
    handleMedia(updated);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(selectedMedia);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setSelectedMedia(items);
    handleMedia(items);
  };

  return (
    <>
      <label className="form-label">Media <span className="text-danger">*</span></label>
      <Select
        mode="multiple"
        placeholder="Select media"
        style={{ width: "100%" }}
        value={selectedMedia.map((m) => m.value)}
        onChange={handleChange}
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.props.children[1]
            .toLowerCase()
            .includes(input.toLowerCase())
        }
      >
        {mediaOptions.map((media) => (
          <Option value={media.value} key={media.value}>
            {media.label}
          </Option>
        ))}
      </Select>
      {showErrors && selectedMedia.length === 0 && (
        <div className="text-danger small mt-1">
          At least one media is required
        </div>
      )}

      <div className="mt-3">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="media-list">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {selectedMedia.map((item, index) => (
                  <Draggable
                    key={item.value}
                    draggableId={item.value.toString()}
                    index={index}
                  >
                    {(dragProvided) => (
                      <div
                        className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded"
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                      >
                        <div className="d-flex align-items-center w-100">
                          <span
                            {...dragProvided.dragHandleProps}
                            className="me-2"
                          >
                            <MenuOutlined />
                          </span>
                          <div className="flex-grow-1 me-3">
                            <div className="fw-semibold">{item.label}</div>
                          </div>
                          {["image", "url", "ppt"].includes(item.type) && (
                            <Tooltip title="Duration (in seconds)">
                              <InputNumber
                                min={1}
                                value={item.duration}
                                onChange={(value) =>
                                  handleDurationChange(index, value)
                                }
                                placeholder="Duration (sec)"
                                style={{ width: 120 }}
                              />
                            </Tooltip>
                          )}
                          {item.type === "video" && (
                            <Tooltip title="Video duration is read from the file itself">
                              <span className="text-muted small">
                                {item.duration_seconds != null
                                  ? `${item.duration_seconds} sec`
                                  : "duration pending"}
                              </span>
                            </Tooltip>
                          )}
                          {item.type === "youtube" && item.duration_seconds != null && (
                            <Tooltip title="Video duration is read from YouTube - see the Media Library's Calculate Video Duration action if it's missing">
                              <span className="text-muted small">
                                {formatDuration(item.duration_seconds)}
                              </span>
                            </Tooltip>
                          )}
                          <Button
                            type="link"
                            danger
                            icon={<MinusCircleOutlined />}
                            onClick={() => handleRemove(index)}
                          />
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </>
  );
};

export default MediaSelector;
